<script setup lang="ts">
/**
 * A link or value to hand to someone else (an admin, another product): shown read-only,
 * selected on focus, with a Copy button. Where the clipboard is refused the value stays
 * on screen to select by hand.
 */
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Check, Copy } from 'lucide-vue-next'

const props = defineProps<{ value: string; label: string }>()

const { t } = useI18n()
const copied = ref(false)

async function copy(): Promise<void> {
  try {
    await navigator.clipboard.writeText(props.value)
    copied.value = true
    setTimeout(() => (copied.value = false), 2000)
  } catch {
    // Selecting it by hand still works.
  }
}
</script>

<template>
  <div class="flex min-w-0 items-center gap-2">
    <input
      :value="value"
      readonly
      :aria-label="label"
      class="min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-2 py-1.5 font-mono text-xs text-gray-700 dark:border-white/15 dark:bg-gray-900 dark:text-gray-200"
      @focus="($event.target as HTMLInputElement).select()"
    />
    <button
      type="button"
      class="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-white/15 dark:bg-white/5 dark:text-gray-200 dark:hover:bg-white/10"
      @click="copy"
    >
      <Check v-if="copied" :size="13" aria-hidden="true" />
      <Copy v-else :size="13" aria-hidden="true" />
      <span aria-live="polite">{{ copied ? t('copied') : t('connectionsCopyLink') }}</span>
    </button>
  </div>
</template>
